import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Admin Pages
import AdminLayout from "./pages/admin/AdminLayout";
import LoginPage from "./pages/admin/LoginPage";
import Dashboard from "./pages/admin/Dashboard";
import FoodsPage from "./pages/admin/FoodsPage";
import RecipesPage from "./pages/admin/RecipesPage";
import RescueActionsPage from "./pages/admin/RescueActionsPage";
import SmartCombosPage from "./pages/admin/SmartCombosPage";
import DailyTipsPage from "./pages/admin/DailyTipsPage";
import AppConfigPage from "./pages/admin/AppConfigPage";
import UsersPage from "./pages/admin/UsersPage";
import WaitlistPage from "./pages/admin/WaitlistPage";
import SystemHealthPage from "./pages/admin/SystemHealthPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AuditLogPage from "./pages/admin/AuditLogPage";
import SyncManagementPage from "./pages/admin/SyncManagementPage";
import AIGeneratePage from "./pages/admin/AIGeneratePage";
import ReviewQueuePage from "./pages/admin/ReviewQueuePage";
import APICredentialsPage from "./pages/admin/APICredentialsPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A6B3C]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Auth Route - redirects to dashboard if already logged in
const AuthRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A6B3C]"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return children;
};

// Home page - redirect to admin
const Home = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate('/admin/dashboard');
  }, [navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-[#1A6B3C] mb-2">CarbiQ</h1>
        <p className="text-gray-500">Content Management System</p>
        <p className="text-sm text-gray-400 mt-4">Redirecting to admin...</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Root redirects to admin */}
            <Route path="/" element={<Home />} />
            
            {/* Admin Login */}
            <Route 
              path="/admin/login" 
              element={
                <AuthRoute>
                  <LoginPage />
                </AuthRoute>
              } 
            />
            
            {/* Admin Routes */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="foods" element={<FoodsPage />} />
              <Route path="recipes" element={<RecipesPage />} />
              <Route path="rescue-actions" element={<RescueActionsPage />} />
              <Route path="combos" element={<SmartCombosPage />} />
              <Route path="tips" element={<DailyTipsPage />} />
              <Route path="config" element={<AppConfigPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="waitlist" element={<WaitlistPage />} />
              <Route path="health" element={<SystemHealthPage />} />
              <Route path="sync" element={<SyncManagementPage />} />
              <Route path="ai-generate" element={<AIGeneratePage />} />
              <Route path="review-queue" element={<ReviewQueuePage />} />
              <Route path="api-credentials" element={<APICredentialsPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              <Route path="audit" element={<AuditLogPage />} />
            </Route>
            
            {/* Catch all - redirect to admin */}
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
